import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@foris/avocado-suite";
import { AppContext } from "../context/EditSessionsContext";
import { EditedSession, ForkWeekLockPredicate, Types } from "../context/formData.reducer";
import { Week } from "../context/linkData.reducer";
import { getOriginalWeeksBySessionId } from "../utils/getOriginalWeeksBySessionId";

/**
 * `runFork` unmarks every checked week matched by `isLocked` in the shared form
 * intervals - which is what the week selector reads - then forks the given
 * sessions and shows the caller's toast. Both fork surfaces share it so the two
 * dispatches always run in the same JS task and in the same order.
 *
 * `sessionsToFork` is explicit because the two surfaces disagree on it: the
 * manual card forks the current selection, while the validate-time modal forks
 * every session of the accumulated edition, which is not necessarily selected
 * (CHS-2818). `isLocked` travels with them so the partition applies the same
 * lock rule the caller used here.
 */
export const useForkOrchestration = () => {
  const { state, dispatch } = useContext(AppContext);
  const { t } = useTranslation();

  const runFork = (
    isLocked: ForkWeekLockPredicate,
    toastKey: string,
    sessionsToFork: EditedSession[],
  ) => {
    const intervals = state?.form?.editedSessions?.intervals ?? [];

    const weeksToDisableCheck: { [id: string]: Week } = {};
    intervals.forEach((week: Week) => {
      const isLockedForEverySession =
        sessionsToFork.length > 0 && sessionsToFork.every(session => isLocked(week, session));

      if (week?.checked && isLockedForEverySession) {
        weeksToDisableCheck[week.id] = { ...week, checked: false };
      }
    });

    const originalWeeksBySessionId = getOriginalWeeksBySessionId(state?.link?.sections ?? []);

    if (Object.keys(weeksToDisableCheck).length > 0) {
      const updatedIntervals = intervals.map((interval: Week) =>
        weeksToDisableCheck[interval.id] ? { ...interval, checked: false } : interval,
      );

      dispatch({
        type: Types.IntervalsEditedSessions,
        payload: {
          intervals: updatedIntervals,
          weeks: weeksToDisableCheck,
          originalWeeksBySessionId,
        },
      });
    }

    dispatch({
      type: Types.ForkSelectedSessions,
      payload: { sessions: sessionsToFork, isLocked },
    });
    toast.success(t(toastKey), { autoClose: 3000 });
  };

  return { runFork };
};
